import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/react";

// A sonda de áudio do LIVE, uma vez por sessão, INDO PARA O SENTRY.
//
// ─── POR QUE ISTO NÃO É UM BREADCRUMB (a correção do LOTE 5) ────────────────
// O pedido original era "mandar a linha de taxas e o resumo de 15 s como
// BREADCRUMB, pelo captureConsoleIntegration". Duas coisas não fecham:
//
//   1. `captureConsoleIntegration` NÃO faz breadcrumb — ela faz EVENTO. E está
//      configurada com `levels: ["error"]` (src/lib/sentry.ts), então só
//      console.error passa por ela. Um console.info não a atravessa.
//   2. Quem transforma console.* em breadcrumb é a `breadcrumbsIntegration`
//      padrão do SDK, que JÁ está ligada — as sondas do LOTE 4.4 já viram
//      breadcrumb hoje, sem nenhum código novo. E não adianta nada: breadcrumb
//      só viaja ANEXADO A UM EVENTO. Numa sessão de LIVE que termina bem não
//      há evento nenhum, e o breadcrumb morre no aparelho junto com a aba.
//
// Ou seja: o item pedido, feito ao pé da letra, entregaria zero do motivo dado
// para ele ("no iPhone ninguém abre DevTools"). O que entrega o motivo é um
// EVENTO próprio, nível info, um por sessão — que é exatamente o padrão que o
// repo já usa para os funis `push_optin_*` (captureMessage + level info + o
// ramo de analítica do sentryBeforeSend, que força info e passa ANTES da
// dedupe para não engolir a segunda sessão dentro de 60 s).
//
// ─── O QUE VAI EM TAG E O QUE VAI EM EXTRA ─────────────────────────────────
// Tag é indexada e filtrável na busca do Sentry; extra não é. Então as
// PERGUNTAS viram tag e as MEDIDAS viram extra. As três perguntas que decidem
// o LOTE 4.4 e este:
//   · `live_mouth_silent` = pico 0 com o modelo falando → o analyser sem saída
//     não roda naquele navegador (o "PARE" do LOTE 4.4).
//   · `live_rate_mismatch` = ctx.sampleRate ≠ taxa da trilha remota →
//     resampling contínuo.
//   · `live_output_route`  = "Speaker" ou não → o som saindo pelo receptor.
// Com isso o aceite deixa de depender do ouvido do Rafa num aparelho só: vira
// uma busca no Sentry sobre a base inteira.

/** Nome do evento. Registrado em ANALYTICS_EVENT_NAMES (src/lib/sentry.ts). */
export const LIVE_AUDIO_EVENT = "mirror_live_audio";

export interface LiveAudioReport {
  /** Taxa do AudioContext próprio da sessão (LOTE 4.4). */
  ctxSampleRate: number | null;
  /** Taxa que a trilha remota do WebRTC declara — normalmente ausente. */
  remoteTrackSampleRate: number | null;
  /** Taxa que a trilha do microfone declara. */
  micTrackSampleRate: number | null;
  ctxBaseLatency: number | null;
  ctxOutputLatency: number | null;
  ctxState: string | null;
  /** Taxa EFETIVA da AVAudioSession nativa, e a pedida (iOS; null no resto). */
  nativeSampleRate: number | null;
  nativePreferredSampleRate: number | null;
  nativeIoBufferDuration: number | null;
  nativeOutputLatency: number | null;
  /** portType da saída ativa: "Speaker", "Receiver", "BluetoothA2DP"… */
  nativeOutputRoute: string | null;
  /** Resumo da janela de 15 s (LOTE 4.1). */
  peak: number | null;
  mean: number | null;
  samplesWithSignal: number | null;
  samplesTotal: number | null;
  /** O modelo falou durante a janela (output_audio_buffer.started). */
  spoke: boolean;
}

/** O analyser leu ZERO com o modelo falando: o critério de reprovação. */
export function mouthWasSilent(r: LiveAudioReport): boolean {
  return r.spoke === true && r.peak !== null && r.peak <= 0;
}

/** ctx e trilha remota em taxas diferentes = resampling contínuo no analyser. */
export function ratesMismatch(r: LiveAudioReport): boolean {
  return (
    r.ctxSampleRate !== null &&
    r.remoteTrackSampleRate !== null &&
    r.ctxSampleRate !== r.remoteTrackSampleRate
  );
}

/** As perguntas, indexadas. Valores string porque tag do Sentry é string. */
export function buildLiveAudioTags(r: LiveAudioReport, platform: string): Record<string, string> {
  return {
    feature: "mirror_live",
    live_platform: platform,
    live_ctx_state: r.ctxState ?? "unknown",
    live_output_route: r.nativeOutputRoute ?? "n/a",
    live_mouth_silent: String(mouthWasSilent(r)),
    live_rate_mismatch: String(ratesMismatch(r)),
    live_spoke: String(r.spoke === true),
  };
}

/** As medidas. Puro para o teste conferir campo a campo sem tocar no SDK. */
export function buildLiveAudioExtra(r: LiveAudioReport): Record<string, unknown> {
  return {
    ctx_sample_rate: r.ctxSampleRate,
    remote_track_sample_rate: r.remoteTrackSampleRate,
    mic_track_sample_rate: r.micTrackSampleRate,
    ctx_base_latency: r.ctxBaseLatency,
    ctx_output_latency: r.ctxOutputLatency,
    ctx_state: r.ctxState,
    native_sample_rate: r.nativeSampleRate,
    native_preferred_sample_rate: r.nativePreferredSampleRate,
    native_io_buffer_duration: r.nativeIoBufferDuration,
    native_output_latency: r.nativeOutputLatency,
    native_output_route: r.nativeOutputRoute,
    peak: r.peak,
    mean: r.mean,
    samples_with_signal: r.samplesWithSignal,
    samples_total: r.samplesTotal,
    spoke: r.spoke,
  };
}

/**
 * Manda a sonda. UMA vez por sessão de LIVE — quem chama é o fim da janela de
 * 15 s em useMirrorLive.
 *
 * Nunca lança: telemetria que derruba a conversa que ela veio medir é pior que
 * telemetria nenhuma.
 */
export function reportLiveAudio(r: LiveAudioReport): void {
  try {
    Sentry.captureMessage(LIVE_AUDIO_EVENT, {
      level: "info",
      tags: buildLiveAudioTags(r, Capacitor.getPlatform()),
      extra: buildLiveAudioExtra(r),
    });
  } catch (e) {
    console.warn("[liveAudioTelemetry] sonda não foi ao Sentry", e);
  }
}
